import { IcpStateDto, LeadsOverviewDto, ToWriteDto } from "@growth/dto";
import { Badge, Button, cn } from "@growth/ui";
import { ArrowRight, Check, ExternalLink, LoaderCircle, PenLine } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { ErrorText, PageHeader } from "../../components/page-header";
import { useActive } from "../../lib/active";
import { apiErrorMessage } from "../../lib/api-error";
import { useIcp } from "../../lib/icp";
import { useCreateDraft, useLeadsOverview, useSendingSettings } from "../../lib/leads";
import { useBrandReadiness } from "../../lib/leads-readiness";
import { BestChannel, card, ContactStatusBadge, SampleBanner, when } from "./shared";

/** Leads home: where the journey stands, and who to write to this week. */
export const LeadsHomePage = () => {
  const { brand } = useActive();
  const overview = useLeadsOverview();
  const settings = useSendingSettings();
  const data = overview.data;
  const day = settings.data?.alerts.weeklyListDay ?? "Monday";

  return (
    <section className="max-w-6xl space-y-6">
      <SampleBanner />
      <PageHeader
        eyebrow={`${brand?.name ?? ""} · Leads`}
        title={
          data
            ? data.toWrite.length
              ? `${data.toWrite.length} ${data.toWrite.length === 1 ? "person" : "people"} to write to this week`
              : "Nobody to chase this week"
            : "Leads"
        }
      >
        Find the people most likely to buy, see why each one fits, and reach them in a way that is
        accurate and allowed. Agents do the research; a person approves every message.
      </PageHeader>
      {overview.isError && <ErrorText>{apiErrorMessage(overview.error)}</ErrorText>}
      {overview.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {data && (
        <>
          <Journey data={data} />
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
            <ToWrite items={data.toWrite} day={day} />
            <aside className="space-y-4">
              <Waiting data={data} />
              <Recent data={data} />
            </aside>
          </div>
        </>
      )}
    </section>
  );
};

const icpStatus = (icp: IcpStateDto | undefined) => {
  if (!icp) return "Checking…";
  if (icp.pending?.status === "drafting") return `Drafting v${icp.pending.version} now`;
  if (icp.pending?.status === "failed") return "The last draft stopped; try again";
  if (icp.draft) return `Draft v${icp.draft.version} is waiting for you`;
  if (icp.inUse) return `v${icp.inUse.version} in use · ${icp.inUse.segments.length} segments`;
  return "Not drafted yet";
};

const Journey = ({ data }: { data: LeadsOverviewDto }) => {
  const readiness = useBrandReadiness();
  const icp = useIcp();
  const steps = [
    {
      label: "Brand understood",
      to: "/setup",
      done: readiness.ready,
      status: readiness.pending
        ? "Checking…"
        : readiness.ready
          ? `Profile and ${readiness.approvedClaims} claims confirmed`
          : `${readiness.toCheck} part${readiness.toCheck === 1 ? "" : "s"} to check`,
    },
    {
      label: "Ideal customer",
      to: "/leads/icp",
      done: Boolean(icp.data?.inUse),
      status: icpStatus(icp.data),
    },
    {
      label: "Accounts",
      to: "/leads/accounts",
      done: data.accounts.total > 0 && data.accounts.toReview === 0,
      status: data.accounts.total
        ? `${data.accounts.total} found · ${data.accounts.tierA} tier A${data.accounts.toReview ? ` · ${data.accounts.toReview} to review` : ""}`
        : "None yet",
    },
    {
      label: "People",
      to: "/leads/people",
      done: data.people.total > 0,
      status: data.people.total
        ? `${data.people.total} people · ${data.people.emailable} can be emailed`
        : "None yet",
    },
    {
      label: "Outreach",
      to: "/leads/outreach",
      done: data.outreach.sent > 0,
      status:
        data.outreach.sent || data.outreach.drafts
          ? `${data.outreach.sent} sent · ${data.outreach.replied} replied${data.outreach.drafts ? ` · ${data.outreach.drafts} to approve` : ""}`
          : "Nothing sent yet",
    },
  ];
  const current = steps.findIndex((step) => !step.done);
  return (
    <ol aria-label="Leads journey" className="grid gap-2 sm:grid-cols-5">
      {steps.map((step, index) => (
        <li key={step.label}>
          <Link
            to={step.to}
            aria-current={index === current ? "step" : undefined}
            className={cn(
              "block h-full space-y-1 border-t-[3px] pt-2 text-sm hover:text-ink",
              step.done ? "border-accent" : index === current ? "border-amber-400" : "border-line",
            )}
          >
            <span
              className={cn(
                "font-medium",
                step.done || index === current ? "text-ink" : "text-muted",
              )}
            >
              {index + 1} · {step.label}
              {step.done && (
                <Check className="ml-1 inline size-3.5 text-accent" aria-label="done" />
              )}
            </span>
            <span className="block text-xs text-muted">{step.status}</span>
          </Link>
        </li>
      ))}
    </ol>
  );
};

const URGENCY: Record<ToWriteDto["urgency"], { label: string; className: string }> = {
  reply: { label: "Answer them", className: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  hot: { label: "Warm", className: "bg-blue-50 text-accent ring-blue-200" },
  linkedin: { label: "LinkedIn", className: "bg-paper text-ink ring-line" },
  follow_up: { label: "Follow-up due", className: "bg-amber-50 text-amber-800 ring-amber-200" },
};

const ToWrite = ({ items, day }: { items: ToWriteDto[]; day: string }) => {
  const navigate = useNavigate();
  const create = useCreateDraft();
  return (
    <section className={cn(card, "p-5")}>
      <div className="mb-3">
        <h2 className="font-serif text-xl text-ink">Who to write to this week</h2>
        <p className="text-xs text-muted">
          Ranked by what people did, counting only people, not mail scanners. Sent to the team every{" "}
          {day} morning.
        </p>
      </div>
      {items.length === 0 ? (
        <p className="py-6 text-sm text-muted">
          Nobody needs a message right now. New replies, real clicks and due follow-ups show up
          here.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {items.map(({ person, reasons, urgency }) => (
            <li key={person.id} className="flex flex-wrap items-start gap-3 py-3">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/leads/people/${person.id}`}
                    className="font-medium text-ink hover:underline"
                  >
                    {person.name}
                  </Link>
                  <Badge className={URGENCY[urgency].className}>{URGENCY[urgency].label}</Badge>
                  <span className="text-xs text-muted">
                    {person.title}, {person.accountName}
                  </span>
                </p>
                <ul className="space-y-0.5 text-xs text-muted">
                  {reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
                <p className="flex flex-wrap items-center gap-3">
                  <ContactStatusBadge status={person.contactStatus} />
                  <BestChannel channel={person.bestChannel} />
                  <span className="text-xs text-muted">Last email {when(person.lastEmailAt)}</span>
                </p>
              </div>
              <div className="flex gap-2">
                {person.bestChannel === "email" ? (
                  <Button
                    variant="secondary"
                    disabled={create.isPending}
                    onClick={() =>
                      create.mutate(person.id, {
                        onSuccess: (id) => navigate(`/leads/outreach/${id}`),
                      })
                    }
                  >
                    <PenLine className="size-4" /> Write
                  </Button>
                ) : person.linkedin.url ? (
                  <Button variant="secondary" asChild>
                    <a href={person.linkedin.url} target="_blank" rel="noreferrer">
                      <ExternalLink className="size-4" /> LinkedIn
                    </a>
                  </Button>
                ) : null}
                <Button variant="ghost" asChild>
                  <Link to={`/leads/people/${person.id}`} aria-label={`Open ${person.name}`}>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {create.isError && <ErrorText>{apiErrorMessage(create.error)}</ErrorText>}
    </section>
  );
};

const Waiting = ({ data }: { data: LeadsOverviewDto }) => {
  const icp = useIcp();
  const rows = [
    icp.data?.draft && {
      to: "/leads/icp",
      label: `ICP draft v${icp.data.draft.version} to check`,
    },
    data.accounts.toReview > 0 && {
      to: "/leads/accounts?status=suggested",
      label: `${data.accounts.toReview} new account${data.accounts.toReview === 1 ? "" : "s"} to review`,
    },
    data.outreach.drafts > 0 && {
      to: "/leads/outreach",
      label: `${data.outreach.drafts} email${data.outreach.drafts === 1 ? "" : "s"} to approve`,
    },
    data.people.linkedinOnly > 0 && {
      to: "/leads/people?channel=linkedin",
      label: `${data.people.linkedinOnly} ${data.people.linkedinOnly === 1 ? "person" : "people"} to reach on LinkedIn by hand`,
    },
  ].filter(Boolean) as { to: string; label: string }[];
  return (
    <section className={cn(card, "space-y-2 p-4")}>
      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">
        Waiting for you
      </h2>
      {rows.length === 0 ? (
        <p className="text-sm text-muted">Nothing waiting.</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((row) => (
            <li key={row.label}>
              <Link
                to={row.to}
                className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm text-ink hover:bg-paper"
              >
                {row.label} <ArrowRight className="size-3.5 text-muted" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const VERB: Record<string, string> = {
  sent: "was emailed",
  opened: "opened your email",
  clicked: "clicked a link",
  visited: "visited the site",
  replied: "replied",
  bounced: "bounced",
  unsubscribed: "unsubscribed",
  linkedin_sent: "got a LinkedIn request",
  linkedin_accepted: "accepted on LinkedIn",
  note: "note",
};

const Recent = ({ data }: { data: LeadsOverviewDto }) => (
  <section className={cn(card, "space-y-2 p-4")}>
    <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">
      Recent, people only
    </h2>
    {data.recent.length === 0 ? (
      <p className="text-sm text-muted">Nothing yet.</p>
    ) : (
      <ul className="space-y-2">
        {data.recent.map((item) => (
          <li key={item.id} className="text-sm">
            <Link
              to={`/leads/people/${item.personId}`}
              className="font-medium text-ink hover:underline"
            >
              {item.personName}
            </Link>{" "}
            <span className="text-ink">{VERB[item.kind] ?? item.kind}</span>
            <span className="block truncate text-xs text-muted" title={item.detail}>
              {item.detail} · {when(item.at)}
            </span>
          </li>
        ))}
      </ul>
    )}
  </section>
);
